import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createPayrollUpload, fetchPayrollBookkeepingProposal } from '~/api/payroll';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { Document, DocumentQueryKeys } from '~/types/document';
import { TransactionRegistrationProposal } from '~/types/general-bookkeeping';
import { PayrollQueryKeys } from '~/types/payroll';
import { formatDateForDisplay } from '~/utils/formatDate';

interface CreatePayrollUploadVariables {
  file: File;
  creationDate: string;
}

export const useCreatePayrollUploadMutation = () => {
  const { captureException } = useExceptionLogger();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({ file, creationDate }: CreatePayrollUploadVariables) =>
      createPayrollUpload(companyId, file, creationDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

const PAYROLL_PROPOSAL_POLL_INTERVAL_MS = 1_000;

export const usePayrollBookkeepingProposalQuery = (documentId: Document['id'], options?: { enabled?: boolean }) => {
  const companyId = useActiveCompanyId();
  const { t } = useTranslation('bookkeeping', {
    keyPrefix: 'general_bookkeeping.non_electronic_documents.payroll_registration',
  });
  const enabled = (options?.enabled ?? true) && !!companyId && !!documentId;

  return useQuery({
    queryKey: PayrollQueryKeys.proposal(companyId, documentId),
    queryFn: () => fetchPayrollBookkeepingProposal(companyId, documentId),
    enabled,
    refetchInterval: (query) => (query.state.data ? false : PAYROLL_PROPOSAL_POLL_INTERVAL_MS),
    select: (data): TransactionRegistrationProposal | null =>
      data
        ? {
            ...data,
            description: t('payroll_description', { date: formatDateForDisplay(data.date) }),
          }
        : null,
  });
};
